/* eslint-disable @typescript-eslint/camelcase */
import { GatewayAbstractService } from '@/services/gateway-abstract-service';
import { getCreditScoreGoodness, isMobile } from '@/common/helpers';
import { DealType } from '@/types/writeup';
import upperFirst from 'lodash/upperFirst';
import mapKeys from 'lodash/mapKeys';
import md5 from 'md5';
import pick from 'lodash/pick';
import { formatCreditAppData } from '@/components/views/credit-application/components/credit-application-form/form-fields';
import { RawLeadData, RequestData, TradeInConditions } from '@/types/lead';

/* eslint-disable @typescript-eslint/camelcase */
export enum FormIds {
	saveAndEmail = 1,
	saveAndText,
	getAssistanceEmail,
	scheduleTestDrive,
	completedDeal,
	additionalSaving,
	tradeIn,
	creditScore,
	lenderSocket,
	reserveVehicle = 10,
}

const DealTypeMap = {
	[DealType.cash]: 'Cash',
	[DealType.loan]: 'Purchase',
	[DealType.lease]: 'Lease',
};

export class LeadService extends GatewayAbstractService {
	sendLead(rawData: RawLeadData, formId: FormIds, token: string | undefined): Promise<object> {
		const data = this.getRequestData(rawData, formId, token);

		return this.gatewayServices.post('lead', data).then(res => res.data);
	}

	private getRequestData(data: RawLeadData, form: FormIds, token: string | undefined) {
		const requestData: RequestData = {
			form: {
				id: form,
				source: 'dr',
			},
			website_id: +data.websiteId,
			// session_id: data.clientId,
			session_id: md5(data.clientId), // we can mix clientId with websiteId if required
			appointment_id: data.appointmentId || 0,
			lead_body: this.getLeadBodyData(data, form),
			request_info: {
				url: window.location.href,
				is_popup: window.top !== window.self,
				is_mobile: isMobile(),
				recaptchaToken: token,
			},
			context: {
				precise_price: {
					primary_color: data.primaryColor,
					vehicle_details_url: data.vehicleDetailsUrl,
					vehicle_dr_image_url: data.writeup.vehicle.imageUrl,
					schedule_test_drive_url: '', // short url of /how-test-drive url of PP 1.0, it does not need now
				},
			},
		};

		if (data.writeup.vehicle.vin) {
			(requestData.context as RequestData).vehicle = { vin: data.writeup.vehicle.vin };
		}

		if (data.writeup.vehicle.dealerId) {
			(requestData.context as RequestData).dealer = { id: data.writeup.vehicle.dealerId };
		}

		if (form === FormIds.lenderSocket) {
			(requestData.context as RequestData).formSubject = 'LenderSocket';
			(requestData.context as RequestData).lender_aggregator = data.lenderSocketServiceProvider;
		}

		return requestData;
	}

	private getLeadBodyData(data: RawLeadData, form: FormIds) {
		const leadData = Object.assign({},
			this.getCommonBodyData(data),
			this.getVehicleData(data),
			this.getFormData(data, form));

		if (form === FormIds.creditScore || form === FormIds.reserveVehicle) {
			delete leadData.protections;
			delete leadData.protections_selected;
		}

		//TODO: Move everything related to lender socket in to separate data formatter
		if (form === FormIds.lenderSocket) {
			delete leadData.protections;
			delete leadData.protections_selected;
			const lenderLeadData: {[id: string]: string|any} = {};

			Object.keys(leadData).map(key => {
				const newKey = key.replace('_', '-');

				lenderLeadData[newKey] = String(leadData[key])
					.replace('$', '')
					.replace('%', '');

				if (newKey === 'term-length') {
					[lenderLeadData[newKey]] = String(leadData[key]).split(' ');
				}
				if (
					newKey === 'income-type'
					|| newKey === 'housingstatus'
					|| newKey === 'currentemploymentinfo-employmentstatus'
					|| newKey === 'previousemploymentinfo-employmentstatus'
					|| newKey === 'spouse-income-type'
					|| newKey === 'spouse-other-income-type'
					|| newKey === 'other-income-type'
				) {
					lenderLeadData[newKey] = String(leadData[key]).replaceAll(' ', '');
					lenderLeadData[newKey] = String(lenderLeadData[newKey]).replaceAll('-', '');
				}
				if (newKey.includes('phone') || newKey.includes('ssn')) {
					lenderLeadData[newKey] = String(leadData[key]).replaceAll('-', '');
				}

				return leadData[key];
			});

			//for lender socket apr should be in 2 decimal
			lenderLeadData.apr = parseFloat(lenderLeadData.apr).toFixed(2);
			return lenderLeadData;
		}

		return leadData;
	}

	private getFormData(data: RawLeadData, form: FormIds) {
		const { formData } = data;
		const keyMap: { [key: string]: string } = {
			firstName: 'firstname',
			lastName: 'lastname',
			email: 'email',
			phoneNumber: 'phone',
			zipCode: 'zipcode',
			phoneType: 'phonetype',
		};
		const fieldsByFormId = {
			[FormIds.completedDeal]: ['firstName', 'lastName', 'phoneNumber', 'email', 'zipCode', 'phoneType'],
		};

		// TODO: extend switch, keyMap, and fieldByFormId for another Forms
		switch (form) {
			case FormIds.additionalSaving:
			case FormIds.completedDeal: {
				return {
					...mapKeys(pick(formData, fieldsByFormId[FormIds.completedDeal]), (value, key) => keyMap[key]),
					net_residual_amount: data.writeup.pricing.netResidual,
					trading_in: data.writeup.tradeIn.tradingIn && data.writeup.tradeIn.tradingIn === 1 ? 'Yes' : 'No',
				};
				break;
			}
			case FormIds.scheduleTestDrive: {
				return {
					...mapKeys(pick(formData, fieldsByFormId[FormIds.completedDeal]), (value, key) => keyMap[key]),
					schedulercalendar: {
						date: formData.date,
						period: formData.time,
						start_time: formData.startTime,
						end_time: formData.endTime,
					},
					customer_address: formData.customerAddress,
					appointment_type: formData.type,
					appointment_reason: formData.appointmentReason,
				};
				break;
			}
			case FormIds.creditScore: {
				return {
					firstname: formData?.firstName,
					lastname: formData?.lastName,
					middlename: formData?.middleName,
					email: formData?.email,
					phone: formData?.phoneNumber,
					address: formData?.address,
					city: formData?.city,
					state: formData?.state,
					zipcode: formData?.zip,
					dateofbirth: formData?.dateOfBirth,
					cbc_application_id: formData?.appId,
					credit_guid: formData?.creditGuid,
				};
				break;
			}
			case FormIds.reserveVehicle: {
				return {
					name: formData?.fullName,
					email: formData?.email,
					address: formData?.address,
					city: formData?.city,
					state: formData?.state,
					zipcode: formData?.zipcode,
					country: formData?.country,
					reserveamount: formData?.amount,
					reserveduration: formData?.reserveduration,
				};
				break;
			}
			case FormIds.lenderSocket: {
				return formatCreditAppData(formData, data);
			}
			default:
				return {};
				break;
		}
	}

	private getVehicleData(data: RawLeadData) {
		if (!data.writeup.vehicle) { return {}; }

		const { vehicle } = data.writeup;

		return {
			vehicle_vin: vehicle.vin,
			vehicle_type: vehicle.isNew ? 'new' : 'used',
			vehicle_year: vehicle.year,
			vehicle_make: vehicle.make,
			vehicle_model: vehicle.model,
			vehicle_trim: vehicle.trim,
			vehicle_price: vehicle.price,
			vehicle_mileage: vehicle.mileage,
		};
	}

	private getCommonBodyData(data: RawLeadData) {
		const { writeup, rebates, protections } = data;
		const leadData: RequestData = {};

		// Payment Terms
		leadData.deal_type = DealTypeMap[writeup.type];

		if (writeup.type === DealType.cash) {
			leadData.purchase_price = `$${writeup.pricing.purchasePrice || 0}`;
		} else {
			leadData.down_payment = `$${Math.floor(writeup.terms.down)}`;
			leadData.term_length = `${writeup.terms.termLength} months`;

			leadData.estimated_credit_score = writeup.terms.isProviderCreditScoreSelected
				? writeup.terms.providerCreditScore : writeup.terms.creditScore;
			leadData.credit_tier = upperFirst(getCreditScoreGoodness(leadData.estimated_credit_score));

			leadData.apr = writeup.terms.apr !== null ? `${writeup.terms.apr}%` : '';

			if (writeup.selectedIncentives?.length) {
				const offer = writeup.selectedIncentives[0];

				leadData.selected_offer = writeup.type === DealType.loan
					? `${offer.term.apr}% FINANCING FOR ${offer.term.months} MONTHS`
					: offer.name;
			}
		}

		// Trade-In Information
		leadData.trading_in = 'No';
		if (writeup.tradeIn.tradingIn && writeup.tradeIn.tradingIn === 1) {
			leadData.trading_in = 'Yes';

			['year', 'make', 'model', 'trim', 'drivetrain', 'transmission', 'engine', 'series', 'style'].forEach(field => {
				if (writeup && writeup.tradeIn && writeup.tradeIn.vehicle && writeup.tradeIn.vehicle[field]) {
					leadData[`tradein_${field}`] = writeup.tradeIn.vehicle[field];
				}
			});

			leadData.tradein_milage = `${Number(writeup.tradeIn.mileage)} miles`;

			if (writeup.tradeIn.questionnaire) {
				const { accident, rental, cond, paid } = writeup.tradeIn.questionnaire;

				if (cond) {
					leadData.tradein_accident = accident ? 'Yes' : 'No';
					leadData.tradein_rental = rental ? 'Yes' : 'No';
					leadData.tradein_cond = TradeInConditions[cond] || '';
				}

				leadData.tradein_paid = paid ? 'Yes' : 'No';
			}

			if (writeup.tradeIn.payoff) {
				leadData.tradein_payoff = `$${Number(writeup.tradeIn.payoff)}`;
			}

			if (writeup.tradeIn.userValue) {
				leadData.tradein_customer_value = `$${Number(writeup.tradeIn.amount)}`;
			} else {
				leadData.tradein_estimated = `$${Number(writeup.tradeIn.amount)}`;
				// TODO: use Settings to set correct book type, webapp code:
				// 	$leadVars['tradein_book_type'] = $this->module->useTradePendingForTradeIn()
				// 		? DigitalRetail_Module::TRADE_IN_TYPE_TRADE_PENDING : app('DrilldownService')->getTradeInBook();
				leadData.tradein_book_type = 'Trade-Pending';
			}
			leadData.tradein_net_trade = `${writeup.pricing.tradeinValue < 0 ? '-' : ''}$${writeup.pricing.tradeinValue}`;
			leadData.tradein_tax = `$${Number(writeup.tradeIn.taxSavings)}`;
		}

		// Discounts and Savings
		// TODO: should we take Ignite option into account or just check Data Availability?
		const selectedRebates = rebates
			.filter(rebate => rebate.selected)
			.map(rebate => `$${this.numberFormat(parseFloat(rebate.cashValue))} ${rebate.title}`);

		if (writeup.dataAvailability.rebates && selectedRebates.length) {
			leadData.rebates = selectedRebates;
		}

		// Taxes & Fees
		if (!writeup.pricing.taxes || !writeup.pricing.fees) {
			leadData.tax = `$${writeup.pricing.taxes || 0}`;
			leadData.fee = `$${writeup.pricing.fees || 0}`;
		}

		// F&I Protections
		// TODO: should we take Ignite option into account or just check Data Availability?
		const selectedProtections = protections.filter(protection => protection.selected).map(protection => {
			const price = protection.totalPrice ? `$${this.numberFormat(protection.totalPrice)}` : '--';
			let { title } = protection;
			let termText = '';

			if (protection.term?.coverage) {
				const { term } = protection;
				const deductible = term.deductible || 'None';
				const duration = term.months ? `${term.months} months` : '';
				const miles = term.miles ? ` / ${term.miles} miles` : '';

				title = term.coverage;
				termText = `[Term: ${duration}${miles}, Deductible: ${deductible}] `;
			}

			return `${title} ${termText}(${price})`;
		});

		if (writeup.dataAvailability.fi && selectedProtections) {
			leadData.protections_selected = selectedProtections ? 'Yes' : 'No';
			leadData.protections = selectedProtections;
		}

		// Interested in accessories
		// TODO: implement customer interests if required
		// Original code:
		// $interestedInAccessories = $this->module->getCustomerService()->getCustomerInterest(CustomerService::INTERESTS_ON_ACCESSORIES);
		//
		// if ($interestedInAccessories !== null) {
		// 	$leadVars['interested_in_accessories'] = $interestedInAccessories ? 'Yes' : 'No';
		// }

		// Advertised Time & Money Savings
		// TODO: implement time/money savings if required
		// Original code:
		// if ($this->module->getSiteConfig()['ShowTimeSavingText']) {
		// 	$leadVars['time_savings'] = $this->module->getTimeSavings();
		// }
		// $leadVars['money_savings'] = '$' . $this->module->getMoneySavings();

		// In-Progress/Finalized Offer
		if (writeup.type !== DealType.cash) {
			if (writeup.type === DealType.loan) {
				leadData.purchase_price = `$${writeup.pricing.purchasePrice || 0}`;
				leadData.total_financed = `$${writeup.pricing.totalPrice || 0}`;
			} else {
				leadData.net_capitalized_cost = `$${writeup.pricing.netCap || 0}`;
			}

			leadData.monthly_payment = `$${writeup.pricing.monthly || 0}`;
		}

		// Write-up in Desking
		leadData.deal_desking_url = '';

		//Personalized URL Sent To Customer
		leadData.dr_url = data.shortUrl;

		return leadData;
	}

	numberFormat(value: number): string {
		return new Intl.NumberFormat('en-US').format(value);
	}
}
